import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import api from '../../services/api';
import { useConfirm } from '../../components/common/ConfirmDialog';

const OwnershipRecordDialog = ({ open, onClose, onSuccess, horse, record, entities, currentTotal, tenantEntry }) => {
  const [formData, setFormData] = useState({
    entity_type: 'account',
    entity_id: '',
    ownership_percent: '',
    date_acquired: '',
    date_sold: '',
    purchase_price: '',
    sale_price: '',
    is_primary_owner: false,
    is_current: true,
    is_notable: false,
    notable_reason: '',
    notes: '',
    is_sold: false
  });
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  useEffect(() => {
    if (record) {
      const hasSaleDate = !!record.date_sold;
      setFormData({
        entity_type: record.entity_type,
        entity_id: record.entity_id,
        ownership_percent: record.ownership_percent,
        date_acquired: record.date_acquired ? format(new Date(record.date_acquired), 'yyyy-MM-dd') : '',
        date_sold: record.date_sold ? format(new Date(record.date_sold), 'yyyy-MM-dd') : '',
        purchase_price: record.purchase_price || '',
        sale_price: record.sale_price || '',
        is_primary_owner: record.is_primary_owner || false,
        is_current: record.is_current,
        is_notable: record.is_notable || false,
        notable_reason: record.notable_reason || '',
        notes: record.notes || '',
        is_sold: !record.is_current || hasSaleDate
      });
    } else {
      setFormData({
        entity_type: 'account',
        entity_id: '',
        ownership_percent: '',
        date_acquired: '',
        date_sold: '',
        purchase_price: '',
        sale_price: '',
        is_primary_owner: false,
        is_current: true,
        is_notable: false,
        notable_reason: '',
        notes: '',
        is_sold: false
      });
    }
  }, [record, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.entity_id || !formData.ownership_percent) {
      toast.error('Please select an owner and enter ownership percentage');
      return;
    }

    const percent = parseFloat(formData.ownership_percent);
    if (formData.is_current) {
      const newTotal = record 
        ? (currentTotal - record.ownership_percent + percent)
        : (currentTotal + percent);
      
      if (newTotal > 100) {
        toast.error(`Total ownership cannot exceed 100%. Current: ${currentTotal}%, Available: ${100 - currentTotal}%`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        horse_id: horse.id,
        entity_type: formData.entity_type,
        entity_id: formData.entity_id,
        ownership_percent: percent,
        date_acquired: formData.date_acquired ? formData.date_acquired ? `${formData.date_acquired}T12:00:00.000Z` : null : null,
        date_sold: formData.date_sold ? formData.date_sold ? `${formData.date_sold}T12:00:00.000Z` : null : null,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
        is_primary_owner: formData.is_primary_owner,
        is_current: formData.is_current,
        is_notable: formData.is_notable,
        notable_reason: formData.notable_reason || null,
        notes: formData.notes || null
      };

      if (record) {
        await api.put(`/ownership-records/${record.id}`, payload);
      } else {
        await api.post('/ownership-records', payload);
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving ownership record:', error);
      toast.error(error.response?.data?.detail || 'Failed to save ownership record');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!await confirm({ title: 'Are you sure you want to delete this ownership record?', destructive: true })) return;

    try {
      await api.delete(`/ownership-records/${record.id}`);
      toast.success('Ownership record deleted');
      onSuccess();
    } catch (error) {
      console.error('Error deleting ownership record:', error);
      toast.error('Failed to delete ownership record');
    }
  };

  const entityList = Object.entries(entities)
    .filter(([_, e]) => e.type === 'account')
    .map(([id, e]) => ({ id, name: e.name }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{record ? 'Edit' : 'Add'} Ownership</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Owner Selection */}
            <div className="space-y-2 col-span-2">
              <Label>Owner *</Label>
              <Select
                value={formData.entity_type === 'tenant' && tenantEntry ? `tenant:${formData.entity_id}` : formData.entity_id}
                onValueChange={(value) => {
                  if (tenantEntry && value === `tenant:${tenantEntry.id}`) {
                    setFormData({ ...formData, entity_id: tenantEntry.id, entity_type: 'tenant' });
                  } else {
                    setFormData({ ...formData, entity_id: value, entity_type: 'account' });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  {entityList.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                  {tenantEntry && (
                    <>
                      <div className="px-2 py-1 text-xs text-muted-foreground border-t mt-1 pt-2">Consignor</div>
                      <SelectItem value={`tenant:${tenantEntry.id}`}>
                        {tenantEntry.name} (Consignor)
                      </SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Ownership Percent */}
            <div className="space-y-2">
              <Label>Ownership % *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.ownership_percent}
                onChange={(e) => setFormData({ ...formData, ownership_percent: e.target.value })}
                placeholder="e.g., 25"
              />
            </div>

            {/* Date Acquired */}
            <div className="space-y-2">
              <Label>Date Acquired</Label>
              <Input
                type="date"
                value={formData.date_acquired}
                onChange={(e) => setFormData({ ...formData, date_acquired: e.target.value })}
              />
            </div>

            {/* Purchase Price */}
            <div className="space-y-2">
              <Label>Purchase Price</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.purchase_price}
                onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                placeholder="Enter amount"
              />
            </div>
          </div>

          {/* Checkboxes */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is-sold"
                checked={formData.is_sold}
                onCheckedChange={(checked) => setFormData({ ...formData, is_sold: checked, is_current: !checked })}
              />
              <Label htmlFor="is-sold" className="text-sm font-normal cursor-pointer">
                Horse was sold by this owner
              </Label>
            </div>
          </div>

          {/* Show Date Sold and Sale Price only if sold checkbox is checked */}
          {formData.is_sold && (
            <div className="grid grid-cols-2 gap-4 pl-6 border-l-2 border-muted">
              {/* Date Sold */}
              <div className="space-y-2">
                <Label>Date Sold</Label>
                <Input
                  type="date"
                  value={formData.date_sold}
                  onChange={(e) => setFormData({ ...formData, date_sold: e.target.value })}
                />
              </div>

              {/* Sale Price */}
              <div className="space-y-2">
                <Label>Sale Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.sale_price}
                  onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                  placeholder="Enter amount"
                />
              </div>
            </div>
          )}

          {/* Other Checkboxes */}
          <div className="space-y-3">

            {formData.is_current && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is-primary"
                  checked={formData.is_primary_owner}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_primary_owner: checked })}
                />
                <Label htmlFor="is-primary" className="text-sm font-normal cursor-pointer">
                  Primary Owner
                </Label>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is-notable"
                checked={formData.is_notable}
                onCheckedChange={(checked) => setFormData({ ...formData, is_notable: checked })}
              />
              <Label htmlFor="is-notable" className="text-sm font-normal cursor-pointer">
                Notable Horse for this Owner
              </Label>
            </div>
          </div>

          {/* Notable Reason */}
          {formData.is_notable && (
            <div className="space-y-2">
              <Label>Notable Reason</Label>
              <Input
                value={formData.notable_reason}
                onChange={(e) => setFormData({ ...formData, notable_reason: e.target.value })}
                placeholder="Why is this horse notable for this owner?"
              />
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
              rows={3}
            />
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            <div>
              {record && (
                <Button 
                  type="button"
                  variant="ghost" 
                  size="icon"
                  onClick={handleDelete}
                  className="text-muted-foreground hover:text-red-600"
                  disabled={saving}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OwnershipRecordDialog;
